import { useEffect, useState } from "react";

import { getSupabase } from "../../lib/supabase";

export type AppUser = {
  id: string;
  email: string | null;
};

export type SessionState = {
  user: AppUser | null;
  loading: boolean;
  /** True if the underlying client failed to initialize (e.g. missing env). */
  error: boolean;
};

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({
    user: null,
    loading: true,
    error: false,
  });

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    try {
      const supabase = getSupabase();
      supabase.auth
        .getSession()
        .then(({ data }) => {
          if (!cancelled) {
            setState({ user: toAppUser(data.session), loading: false, error: false });
          }
        })
        .catch(() => {
          if (!cancelled) setState({ user: null, loading: false, error: true });
        });

      const { data } = supabase.auth.onAuthStateChange((_event, next) => {
        if (!cancelled) {
          setState({ user: toAppUser(next), loading: false, error: false });
        }
      });
      unsubscribe = () => data.subscription.unsubscribe();
    } catch {
      // getSupabase() throws synchronously when env vars are missing.
      // Surface as an error state so consumers can distinguish "not signed in"
      // from "auth client unavailable" instead of hanging on loading=true.
      // Defer to a microtask so the setState happens after the effect body.
      queueMicrotask(() => {
        if (!cancelled) setState({ user: null, loading: false, error: true });
      });
    }

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return state;
}

function toAppUser(
  session: { user?: { id: string; email?: string | null } } | null,
): AppUser | null {
  if (!session?.user) return null;
  return { id: session.user.id, email: session.user.email ?? null };
}
